/**
 * Storage Dev Tools Page
 *
 * Debug/validation page for the file storage backend.
 * Platform admins only. Function over form — raw API responses.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useFileStore, UploadProgress } from '@/stores/files/file-store';
import { FileService, FileRecord } from '@/services/api/fileService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Upload, Download, Trash2, RefreshCw, Database, Shield, Eye } from 'lucide-react';
import * as errorUtils from '@/utils/errors';

type UploadScope = 'private' | 'shared';
type UploadEncryptionType = 'none' | 'org' | 'user_protected' | 'per_file';

function parseUploadScope(value: string): UploadScope {
  return value === 'private' || value === 'shared' ? value : 'private';
}

// ============================================================================
// Section: Connection & Health
// ============================================================================

const HealthSection: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const [healthResult, setHealthResult] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const checkHealth = useCallback(async () => {
    setLoading(true);
    try {
      const response = await FileService.getUsage(orgSlug);
      setHealthResult(JSON.stringify(response, null, 2));
    } catch (error: unknown) {
      setHealthResult(JSON.stringify({ error: errorUtils.getErrorMessageOrFallback(error, 'Health check failed') }, null, 2));
    }
    setLoading(false);
  }, [orgSlug]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={checkHealth} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Check Health & Usage
        </Button>
        <span className="text-sm text-muted-foreground">Org: {orgSlug}</span>
      </div>
      {healthResult && (
        <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-64 font-mono">
          {healthResult}
        </pre>
      )}
    </div>
  );
};

// ============================================================================
// Section: Quick Upload Test
// ============================================================================

const UploadTestSection: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [encryptionType, setEncryptionType] = useState<UploadEncryptionType>('none');
  const [passphrase, setPassphrase] = useState('');
  const [scope, setScope] = useState<UploadScope>('private');
  const [uploadResult, setUploadResult] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setUploadResult('');

    try {
      // Step 1: Get presigned URL
      setUploadResult('Step 1: Requesting upload URL...\n');
      const urlResponse = await FileService.requestUploadUrl(orgSlug, {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        scope,
        encryptionType,
        passphrase: encryptionType === 'user_protected' ? passphrase : undefined,
      });
      setUploadResult((prev) => prev + JSON.stringify(urlResponse.data, null, 2) + '\n\n');
      const uploadData = urlResponse.data;
      if (!uploadData?.uploadUrl || !uploadData.fileId) {
        throw new Error('Upload URL response was missing uploadUrl or fileId');
      }

      // Step 2: Upload to presigned URL
      setUploadResult((prev) => prev + 'Step 2: Uploading to presigned URL...\n');
      const { etag } = await FileService.uploadToPresignedUrl(
        uploadData.uploadUrl,
        file,
        file.type || 'application/octet-stream',
        uploadData.headers,
        (p) => setProgress(p)
      );
      setUploadResult((prev) => prev + `Upload complete. etag: ${etag}\n\n`);

      // Step 3: Confirm
      setUploadResult((prev) => prev + 'Step 3: Confirming upload...\n');
      const confirmResponse = await FileService.confirmUpload(orgSlug, uploadData.fileId, etag);
      setUploadResult((prev) => prev + JSON.stringify(confirmResponse.data, null, 2));
    } catch (error: unknown) {
      setUploadResult(
        (prev) =>
          prev +
          `\nERROR: ${JSON.stringify(
            errorUtils.getApiErrorData(error) ?? errorUtils.getErrorMessage(error),
            null,
            2,
          )}`,
      );
    }
    setUploading(false);
  }, [orgSlug, encryptionType, passphrase, scope]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileInputRef} type="file" className="text-sm" />
        <select
          value={scope}
          onChange={(e) => setScope(parseUploadScope(e.target.value))}
          className="text-sm border rounded px-2 py-1 bg-background"
        >
          <option value="private">Private</option>
          <option value="shared">Shared</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">Encryption:</span>
        {(['none', 'org', 'user_protected', 'per_file'] as const).map((type) => (
          <label key={type} className="flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name="encryption"
              value={type}
              checked={encryptionType === type}
              onChange={() => setEncryptionType(type)}
            />
            {type}
          </label>
        ))}
      </div>

      {encryptionType === 'user_protected' && (
        <Input
          type="password"
          placeholder="Passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          className="max-w-xs"
        />
      )}

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={handleUpload} disabled={uploading}>
          <Upload className="w-4 h-4 mr-1" />
          Upload Test
        </Button>
        {uploading && <Progress value={progress} className="w-48" />}
      </div>

      {uploadResult && (
        <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-96 font-mono whitespace-pre-wrap">
          {uploadResult}
        </pre>
      )}
    </div>
  );
};

// ============================================================================
// Section: File Browser (Raw)
// ============================================================================

const FileBrowserSection: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string>('');

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await FileService.listFiles(orgSlug, { limit: 50 });
      setFiles(response.data || []);
    } catch (error: unknown) {
      setActionResult(
        `Error: ${errorUtils.getErrorMessageOrFallback(error, 'Failed to load files')}`,
      );
    }
    setLoading(false);
  }, [orgSlug]);

  const handleDownload = useCallback(async (fileId: string, encType: string) => {
    try {
      let passphrase: string | undefined;
      if (encType === 'user_protected') {
        passphrase = prompt('Enter passphrase for download:') || undefined;
        if (!passphrase) return;
      }
      const response = await FileService.getDownloadUrl(orgSlug, fileId, passphrase);
      if (response.data?.downloadUrl) {
        const downloadWindow = window.open(response.data.downloadUrl, '_blank', 'noopener,noreferrer');
        if (downloadWindow) {
          downloadWindow.opener = null;
        }
        setActionResult(`Download URL: ${response.data.downloadUrl}`);
      } else {
        setActionResult('Download error: response was missing downloadUrl');
      }
    } catch (error: unknown) {
      setActionResult(
        `Download error: ${JSON.stringify(
          errorUtils.getApiErrorData(error) ??
            errorUtils.getErrorMessageOrFallback(error, 'Download failed'),
        )}`,
      );
    }
  }, [orgSlug]);

  const handleDelete = useCallback(async (fileId: string) => {
    if (!confirm('Delete this file?')) return;
    try {
      await FileService.deleteFile(orgSlug, fileId);
      setFiles((prev) => prev.filter((f) => f._id !== fileId));
      setActionResult(`Deleted: ${fileId}`);
    } catch (error: unknown) {
      setActionResult(
        `Delete error: ${errorUtils.getErrorMessageOrFallback(error, 'Delete failed')}`,
      );
    }
  }, [orgSlug]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      <Button size="sm" onClick={fetchFiles} disabled={loading}>
        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
        Load Files ({files.length})
      </Button>

      {files.length > 0 && (
        <div className="border rounded overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted text-left">
                <th className="p-2">Filename</th>
                <th className="p-2">Size</th>
                <th className="p-2">Provider</th>
                <th className="p-2">Encryption</th>
                <th className="p-2">Thumbs</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <React.Fragment key={file._id}>
                  <tr className="border-t hover:bg-muted/50 cursor-pointer" onClick={() => setExpandedId(expandedId === file._id ? null : file._id)}>
                    <td className="p-2 font-mono text-xs">{file.filename}</td>
                    <td className="p-2">{formatSize(file.size)}</td>
                    <td className="p-2"><Badge variant="outline">{file.storageProvider}</Badge></td>
                    <td className="p-2"><Badge variant={file.encryptionType === 'none' ? 'outline' : 'default'}>{file.encryptionType}</Badge></td>
                    <td className="p-2"><Badge variant="outline">{file.thumbnailStatus}</Badge></td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDownload(file._id, file.encryptionType); }}>
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(file._id); }}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === file._id && (
                    <tr>
                      <td colSpan={6} className="p-2 bg-muted/30">
                        <pre className="text-xs font-mono overflow-auto max-h-48">
                          {JSON.stringify(file, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionResult && (
        <pre className="bg-muted p-2 rounded text-xs font-mono overflow-auto max-h-32">
          {actionResult}
        </pre>
      )}
    </div>
  );
};

// ============================================================================
// Section: Quota & Usage
// ============================================================================

const QuotaSection: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const [usageResult, setUsageResult] = useState<string>('');
  const [usagePercentage, setUsagePercentage] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await FileService.getUsage(orgSlug);
      setUsageResult(JSON.stringify(response.data, null, 2));
      setUsagePercentage(response.data?.usagePercentage || 0);
    } catch (error: unknown) {
      setUsageResult(
        JSON.stringify(
          { error: errorUtils.getErrorMessageOrFallback(error, 'Usage check failed') },
          null,
          2,
        ),
      );
    }
    setLoading(false);
  }, [orgSlug]);

  return (
    <div className="space-y-3">
      <Button size="sm" onClick={fetchUsage} disabled={loading}>
        <Database className="w-4 h-4 mr-1" />
        Fetch Usage
      </Button>
      {usagePercentage > 0 && (
        <div className="flex items-center gap-3">
          <Progress value={usagePercentage} className="w-64" />
          <span className="text-sm font-medium">{usagePercentage.toFixed(1)}%</span>
        </div>
      )}
      {usageResult && (
        <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-64 font-mono">
          {usageResult}
        </pre>
      )}
    </div>
  );
};

// ============================================================================
// Section: Thumbnail Inspector
// ============================================================================

const ThumbnailSection: React.FC<{ orgSlug: string }> = ({ orgSlug }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [thumbFile, setThumbFile] = useState<FileRecord | null>(null);
  const [thumbUrls, setThumbUrls] = useState<{ small?: string; medium?: string; large?: string }>({});
  const [status, setStatus] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUploadAndInspect = useCallback(async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    setUploading(true);
    setStatus('Uploading image...');
    setThumbFile(null);
    setThumbUrls({});

    try {
      // Upload
      const urlResp = await FileService.requestUploadUrl(orgSlug, {
        filename: file.name,
        contentType: file.type,
        size: file.size,
        scope: 'shared',
      });
      const uploadData = urlResp.data;
      if (!uploadData?.uploadUrl || !uploadData.fileId) {
        throw new Error('Upload URL response was missing uploadUrl or fileId');
      }

      const { etag } = await FileService.uploadToPresignedUrl(
        uploadData.uploadUrl,
        file,
        file.type,
        uploadData.headers
      );
      const confirmResp = await FileService.confirmUpload(orgSlug, uploadData.fileId, etag);
      const uploaded = confirmResp.data;
      if (!uploaded) {
        throw new Error('Upload confirmation response was missing file data');
      }
      setThumbFile(uploaded);
      setStatus(`File uploaded. Thumbnail status: ${uploaded.thumbnailStatus}. Polling...`);

      // Poll for thumbnail completion
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const fileResp = await FileService.getFile(orgSlug, uploaded._id);
          const f = fileResp.data;
          if (!f) {
            setStatus(`Poll ${attempts}: file response missing data`);
            if (attempts > 20) {
              clearInterval(poll);
            }
            return;
          }
          setThumbFile(f);
          setStatus(`Poll ${attempts}: thumbnailStatus = ${f.thumbnailStatus}`);

          if (f.thumbnailStatus === 'complete' || f.thumbnailStatus === 'failed' || attempts > 20) {
            clearInterval(poll);
            if (f.thumbnails) {
              setThumbUrls(f.thumbnails);
            }
            setStatus(`Final status: ${f.thumbnailStatus}`);
          }
        } catch {
          if (attempts > 20) clearInterval(poll);
        }
      }, 2000);
    } catch (error: unknown) {
      setStatus(
        `Error: ${errorUtils.getErrorMessageOrFallback(error, 'Status check failed')}`,
      );
    }
    setUploading(false);
  }, [orgSlug]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <input ref={fileInputRef} type="file" accept="image/*" className="text-sm" />
        <Button size="sm" onClick={handleUploadAndInspect} disabled={uploading}>
          <Eye className="w-4 h-4 mr-1" />
          Upload & Inspect Thumbnails
        </Button>
      </div>
      {status && <p className="text-sm text-muted-foreground">{status}</p>}
      {thumbFile && (
        <pre className="bg-muted p-2 rounded text-xs font-mono overflow-auto max-h-32">
          {JSON.stringify({ thumbnails: thumbFile.thumbnails, thumbnailStatus: thumbFile.thumbnailStatus }, null, 2)}
        </pre>
      )}
      {(thumbUrls.small || thumbUrls.medium || thumbUrls.large) && (
        <div className="flex gap-4">
          {thumbUrls.small && (
            <div className="text-center">
              <p className="text-xs mb-1">Small (150px)</p>
              <img src={thumbUrls.small} alt="small" className="border rounded max-w-[150px]" />
            </div>
          )}
          {thumbUrls.medium && (
            <div className="text-center">
              <p className="text-xs mb-1">Medium (600px)</p>
              <img src={thumbUrls.medium} alt="medium" className="border rounded max-w-[300px]" />
            </div>
          )}
          {thumbUrls.large && (
            <div className="text-center">
              <p className="text-xs mb-1">Large (1200px)</p>
              <img src={thumbUrls.large} alt="large" className="border rounded max-w-[400px]" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Section: Admin Overview
// ============================================================================

const AdminOverviewSection: React.FC = () => {
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await FileService.getAdminOverview();
      setResult(JSON.stringify(response.data, null, 2));
    } catch (error: unknown) {
      setResult(
        JSON.stringify(
          { error: errorUtils.getErrorMessageOrFallback(error, 'Overview request failed') },
          null,
          2,
        ),
      );
    }
    setLoading(false);
  }, []);

  return (
    <div className="space-y-3">
      <Button size="sm" onClick={fetchOverview} disabled={loading}>
        <Shield className="w-4 h-4 mr-1" />
        Fetch Admin Overview
      </Button>
      {result && (
        <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-64 font-mono">
          {result}
        </pre>
      )}
    </div>
  );
};

// ============================================================================
// Main Page
// ============================================================================

export const DevStoragePage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { platformAdminEntitlement, currentContext, organizations } = useOrganizationStore();
  const isAdmin = platformAdminEntitlement;

  const orgSlug = currentContext?.type === 'organization' ? currentContext.orgSlug || '' : '';
  const currentOrganization = orgSlug
    ? organizations.find((org) => org.slug === orgSlug)
    : undefined;

  if (!user || !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Access denied. Platform admin required.</div>
      </div>
    );
  }

  if (!orgSlug) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">No organization selected. Select an org to test storage.</div>
      </div>
    );
  }

  const sections = [
    { title: 'Connection & Health', icon: RefreshCw, content: <HealthSection orgSlug={orgSlug} /> },
    { title: 'Quick Upload Test', icon: Upload, content: <UploadTestSection orgSlug={orgSlug} /> },
    { title: 'File Browser (Raw)', icon: Database, content: <FileBrowserSection orgSlug={orgSlug} /> },
    { title: 'Quota & Usage', icon: Database, content: <QuotaSection orgSlug={orgSlug} /> },
    { title: 'Thumbnail Inspector', icon: Eye, content: <ThumbnailSection orgSlug={orgSlug} /> },
    { title: 'Admin Overview', icon: Shield, content: <AdminOverviewSection /> },
  ];

  return (
    <div className="fixed inset-0 top-[73px] z-10 bg-background flex flex-col overflow-auto">
      <div className="p-6 max-w-6xl mx-auto w-full space-y-4">
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-6 h-6" />
          <h1 className="text-2xl font-bold">Storage Dev Tools</h1>
          <Badge variant="outline">Org: {orgSlug}</Badge>
          <Badge variant="outline">{currentOrganization?.billingPlan?.type || 'no plan'}</Badge>
        </div>

        {sections.map((section) => (
          <Collapsible key={section.title} defaultOpen={false}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors text-left">
              <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              <section.icon className="w-4 h-4" />
              <span className="font-medium">{section.title}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="p-4 border border-t-0 rounded-b-lg">
              {section.content}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );
};

export default DevStoragePage;
